import { useState } from 'react';
import { ChevronDown, SearchX, ShieldOff } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@voll-intelligence/ui-kit/ui/collapsible';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@voll-intelligence/ui-kit/ui/empty';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import type {
  HotelOfferView,
  HotelRateChip,
  HotelRateTypeKey,
  HotelRoomReceiptView,
  HotelRoomsContextView,
  HotelRoomView,
} from '../models.js';
import { HotelOfferContent } from '../results/hotel_offer_card.js';
import { HotelRoomDetailModal } from './hotel_room_detail_modal.js';
import { HotelRoomGroup } from './hotel_room_group.js';
import {
  filterRooms,
  HotelRoomFilterStrip,
  type HotelRoomFilter,
  type HotelRoomFilterState,
} from './hotel_room_filters.js';

export type { HotelRoomFilter } from './hotel_room_filters.js';

export type HotelRoomsCopy = {
  title: string;
  negotiatedLabel: string;
  withinPolicyFilterLabel: string;
  breakfastFilterLabel: string;
  refundableFilterLabel: string;
  previousFilterLabel: string;
  nextFilterLabel: string;
  noFilteredRoomsLabel: string;
  noFilteredRoomsHint: string;
  clearFiltersLabel: string;
  /** A linha que confirma o toque num chip que não mexeu em nada à vista
   *  (#6470): todo quarto da lista já atende a ele. Recebe o rótulo do chip. */
  allRoomsMeetFilterLabel(filter: string): string;
  roomDetailsLabel: string;
  roomAmenitiesLabel: string;
  previousPhotoLabel: string;
  nextPhotoLabel: string;
  selectRateLabel: string;
  selectedRateLabel: string;
  selectedNotePrefix: string;
  showBenefitsLabel: string;
  otherRatesLabel: string;
  ratesAriaLabel: string;
  seeMoreRatesLabel(count: number): string;
  seeLessRatesLabel: string;
  scarcityLabel(count: number): string;
  outOfPolicyLabel: string;
  outOfPolicyHint: string;
  noRatesLabel: string;
  closeLabel: string;
  /** Um rótulo por valor de chip da tarifa (ver `HotelRateChip`): o kit escolhe
   *  ícone e cor, o host traz a palavra. */
  rateChipLabels: Record<HotelRateChip, string>;
  /** Um rótulo por TIPO de tarifa (ver `HotelRateTypeKey`): o header de cada
   *  caixa do painel — o kit deriva o tipo, o host traz a palavra. */
  rateTypeLabels: Record<HotelRateTypeKey, string>;
  /** O rótulo do porão (`otherRooms`): quartos com nome ilegível, à parte da
   *  lista principal. Obrigatório mesmo quando o bloco não tem quarto não
   *  classificado — nenhum fallback embutido substitui a cópia do host. */
  otherOptionsLabel(count: number): string;
};

/** A régua que julga a lista, na outra ponta da linha do título: `⛨ Acima de
 *  R$ X`, uma unidade só, com a explicação no popover — a forma compacta do
 *  aviso do teto do design hub. Só existe quando o dado da política chega. */
function PolicyCeilingNotice({
  notice,
}: {
  readonly notice: NonNullable<HotelRoomsContextView['policyCeiling']>;
}) {
  return (
    <span className="text-caption text-muted-foreground flex min-w-0 items-center font-semibold">
      <Popover>
        {/* Escudo DENTRO do gatilho: fora dele, o glifo era a única parte do
            aviso que não abria a explicação, e é ele que o olho encontra
            primeiro. Mesmo 16px da marca dos cards. */}
        <PopoverTrigger
          openOnHover
          delay={120}
          render={
            <button
              type="button"
              className="flex min-w-0 cursor-pointer items-center gap-1.5 tabular-nums"
            >
              <ShieldOff className="text-(--destructive) size-4 shrink-0" aria-hidden />
              {/* `truncate` no VALOR e `shrink-0` no escudo: o header promete que
                  quem cede no apertado é o aviso, e com `nowrap` ele não cedia —
                  empurrava a linha para fora. O escudo nunca some, e o teto
                  inteiro segue no popover. */}
              <span className="truncate underline underline-offset-3">{notice.label}</span>
            </button>
          }
        />
        <PopoverContent className="text-caption grid w-60 gap-1">
          <span className="text-muted-foreground">{notice.hint}</span>
        </PopoverContent>
      </Popover>
    </span>
  );
}

/** Quem abre o card: o recibo nomeia a reserva; a escolha abre pelo hotel
 *  escolhido. Sem `selectedHotel` sobra só a linha do título — é o que acontece
 *  em bloco antigo, gravado antes do card. */
function HotelRoomsHeader({
  receipt,
  selectedHotel,
  context,
  copy,
}: {
  readonly receipt?: HotelRoomReceiptView;
  readonly selectedHotel?: HotelOfferView;
  readonly context?: HotelRoomsContextView;
  readonly copy: HotelRoomsCopy;
}) {
  if (receipt) {
    return (
      <header className="grid gap-1">
        <h2 className="text-heading">{receipt.hotelName}</h2>
        {receipt.stayLine && (
          <p data-stay-line className="text-caption text-muted-foreground">
            {receipt.stayLine}
          </p>
        )}
      </header>
    );
  }
  return (
    <>
      {selectedHotel && (
        <div
          className="bg-card ring-foreground/10 grid gap-2 rounded-xl p-3 ring-1"
          data-surface="plain"
        >
          <HotelOfferContent offer={selectedHotel} />
        </div>
      )}
      {/* Título e aviso do teto na mesma linha. `whitespace-nowrap` + `shrink-0`
          no título: quem cede no apertado é o AVISO, nunca o h2. */}
      <header className="flex min-h-8 items-center justify-between gap-3">
        <h2 className="text-heading shrink-0 whitespace-nowrap">{copy.title}</h2>
        {context?.policyCeiling ? <PolicyCeilingNotice notice={context.policyCeiling} /> : null}
      </header>
    </>
  );
}

/** O estado vazio com a saída na mão: constatar e não oferecer caminho deixa a
 *  pessoa presa — o caminho de volta é soltar os filtros, então o botão faz
 *  isso. `border` porque o primitivo nasce com `border-dashed` de largura zero. */
function NoFilteredRooms({
  copy,
  onClear,
}: {
  readonly copy: HotelRoomsCopy;
  readonly onClear: () => void;
}) {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <SearchX aria-hidden />
        </EmptyMedia>
        <EmptyTitle>{copy.noFilteredRoomsLabel}</EmptyTitle>
        <EmptyDescription>{copy.noFilteredRoomsHint}</EmptyDescription>
      </EmptyHeader>
      <Button variant="outline" size="sm" onClick={onClear}>
        {copy.clearFiltersLabel}
      </Button>
    </Empty>
  );
}

/** Os spreads condicionais do cabeçalho, fora do componente pelo teto de
 *  complexidade: com `exactOptionalPropertyTypes`, cada opcional ausente vira
 *  um ternário — e três deles custavam o orçamento do `HotelRooms`. */
function headerProps(
  receiptView: HotelRoomReceiptView | undefined,
  selectedHotel: HotelOfferView | undefined,
  context: HotelRoomsContextView | undefined,
) {
  return {
    ...(receiptView ? { receipt: receiptView } : {}),
    ...(selectedHotel ? { selectedHotel } : {}),
    ...(context ? { context } : {}),
  };
}

/** O cabeçalho da lista atrás do `hideHeader` — slot próprio para o ramo não
 *  pesar no cap de complexidade do `HotelRooms` (ver a prop em
 *  `HotelRoomsSharedProps`). */
function HotelRoomsHeaderSlot({
  hide,
  receiptView,
  selectedHotel,
  context,
  copy,
}: {
  readonly hide: boolean;
  readonly receiptView: HotelRoomReceiptView | undefined;
  readonly selectedHotel: HotelOfferView | undefined;
  readonly context: HotelRoomsContextView | undefined;
  readonly copy: HotelRoomsCopy;
}) {
  if (hide) return null;
  return <HotelRoomsHeader {...headerProps(receiptView, selectedHotel, context)} copy={copy} />;
}

/** Os spreads condicionais do porão, fora do `HotelRooms` pelo mesmo teto de
 *  complexidade que isolou `headerProps` — `exactOptionalPropertyTypes` cobra
 *  um ternário por opcional ausente. */
function otherRoomsProps(
  context: HotelRoomsContextView | undefined,
  selectedRateId: string | undefined,
) {
  return {
    ...(context ? { context } : {}),
    ...(selectedRateId ? { selectedRateId } : {}),
  };
}

/** O porão do spec: SÓ nomes ilegíveis/placeholder SEM tarifa negociada chegam
 *  aqui (R6; a de acordo fica na lista principal, #5922 — a regra é do
 *  contrato, `isOtherRoomsGroup`, e o kit só recebe a lista pronta). Colapsada
 *  por padrão — o viajante decide olhar; nada é apagado. Recebe a lista
 *  inteira (mesmo vazia/ausente) e decide sozinha se há algo pra mostrar —
 *  assim quem chama nunca precisa de um ternário próprio pra isso. */
function OtherRoomsSection({
  rooms,
  copy,
  disabled,
  context,
  selectedRateId,
  selectionConfirmed,
  onRateSelect,
  onDetailsOpen,
}: {
  readonly rooms: readonly HotelRoomView[] | undefined;
  readonly copy: HotelRoomsCopy;
  readonly disabled: boolean;
  readonly context?: HotelRoomsContextView;
  readonly selectedRateId?: string;
  /** Ver `HotelRoomGroup.selectionConfirmed` — repassado como está (sem
   *  default aqui: um opcional ausente custaria mais um ramo no teto de
   *  complexidade do `HotelRooms`; o default mora em `roomCardState`). */
  readonly selectionConfirmed: boolean | undefined;
  readonly onRateSelect: (roomId: string, rateId: string) => void;
  readonly onDetailsOpen: (roomId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!rooms || rooms.length === 0) return null;
  const label = copy.otherOptionsLabel(rooms.length);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        render={<Button variant="ghost" className="justify-start gap-2 text-muted-foreground" />}
      >
        <ChevronDown
          className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
        {label}
      </CollapsibleTrigger>
      <CollapsibleContent className="grid gap-6 pt-4">
        {rooms.map((room) => (
          <HotelRoomGroup
            key={room.id}
            room={room}
            copy={copy}
            {...(context ? { context } : {})}
            {...(selectedRateId ? { selectedRateId } : {})}
            selectionConfirmed={selectionConfirmed}
            receipt={false}
            disabled={disabled}
            onRateSelect={onRateSelect}
            onDetailsOpen={onDetailsOpen}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

type HotelRoomsSharedProps = {
  rooms: readonly HotelRoomView[];
  /** O hotel escolhido, como card, abrindo o turno. Sem ele o card de quartos
   *  abre pelo título — é o que acontece no recibo e em bloco antigo. */
  selectedHotel?: HotelOfferView;
  copy: HotelRoomsCopy;
  context?: HotelRoomsContextView;
  selectedRateId?: string;
  /** `selectedRateId` já foi CONFIRMADO (recibo, ou seleção em andamento pro
   *  checkout) ou é só a SUGESTÃO do agente (quarto citado na conversa, ainda
   *  por escolher)? Default `true` preserva o travamento de sempre; `false`
   *  revela a tarifa sem travar o CTA de seleção. */
  selectionConfirmed?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  /** A lista SEM o próprio cabeçalho — a Página do Hotel com a fileira da
   *  estadia desenha o título sólido por fora (designer, 2026-08-24), e um
   *  segundo título aqui leria como duas seções. Só o título sai: filtros,
   *  aviso e cards seguem daqui. */
  hideHeader?: boolean;
  /** Chips que já nascem ligados — o recorte que o host aplicou por conta da
   *  pessoa (o pedido "quero com café" dito na conversa). Só o estado INICIAL:
   *  depois disso os chips são dela. */
  initialFilters?: readonly HotelRoomFilter[];
  onRateSelect(roomId: string, rateId: string): void;
  onRoomDetailsOpen(roomId: string): void;
};

type HotelRoomsSelectionProps = HotelRoomsSharedProps & {
  display?: 'selection';
  receipt?: never;
  /** O porão (`hotelRooms.otherOptions`): quartos com nome ilegível e sem
   *  tarifa negociada, colapsados numa seção própria. Nunca no recibo — a
   *  reserva já fechou num quarto só. */
  otherRooms?: readonly HotelRoomView[];
};

type HotelRoomsReceiptProps = HotelRoomsSharedProps & {
  display: 'receipt';
  receipt: HotelRoomReceiptView;
};

export type HotelRoomsProps = HotelRoomsSelectionProps | HotelRoomsReceiptProps;

/** The room whose details are open: one that leaves the list closes them (during
 *  render), and does not reopen if it comes back. */
function useDetailRoom(allRooms: readonly HotelRoomView[]) {
  const [detailRoomId, setDetailRoomId] = useState<string>();
  const detailRoom = allRooms.find((room) => room.id === detailRoomId);
  if (detailRoomId !== detailRoom?.id) setDetailRoomId(undefined);
  return [detailRoom, setDetailRoomId] as const;
}

export function HotelRooms(props: Readonly<HotelRoomsProps>) {
  const {
    rooms,
    selectedHotel,
    copy,
    context,
    selectedRateId,
    selectionConfirmed,
    readOnly = false,
    busy = false,
    onRateSelect,
    onRoomDetailsOpen,
  } = props;
  const hideHeader = Boolean(props.hideHeader);
  const [filterState, setFilterState] = useState<HotelRoomFilterState>(() => ({
    filters: [...(props.initialFilters ?? [])],
    toggled: undefined,
  }));
  const { filters } = filterState;
  const receipt = props.display === 'receipt';
  const receiptView = receipt ? props.receipt : undefined;
  const otherRooms = props.display === 'receipt' ? undefined : props.otherRooms;
  /* O porão é procurável por detalhes como qualquer outro quarto — sem isto,
     abrir "Ver detalhes" numa linha de `OtherRoomsSection` seta um id que
     nenhum `.find` em `rooms` encontra, o modal nunca abre e o
     `useDetailRoom` zera o id no mesmo render. */
  const allRooms = [...rooms, ...(otherRooms ?? [])];
  const [detailRoom, setDetailRoomId] = useDetailRoom(allRooms);
  const disabled = readOnly || busy || receipt;
  const filtersAvailable =
    !receipt &&
    allRooms.some((room) =>
      room.rates.some(
        (rate) =>
          rate.policyTone !== 'unknown' ||
          rate.breakfast !== undefined ||
          rate.refund !== undefined,
      ),
    );
  const visibleRooms = filterRooms(rooms, filters);
  const visibleOtherRooms = filterRooms(otherRooms, filters);

  return (
    /* Título, filtros, cards — nessa ordem, num grid só: os filtros operam
       sobre o que vem embaixo, não sobre o título. */
    <section
      className="genui2 grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6 pt-2"
      aria-label={copy.title}
      aria-busy={busy || undefined}
    >
      <HotelRoomsHeaderSlot
        hide={hideHeader}
        receiptView={receiptView}
        selectedHotel={selectedHotel}
        context={context}
        copy={copy}
      />
      {filtersAvailable && (
        <HotelRoomFilterStrip
          rooms={allRooms}
          state={filterState}
          copy={copy}
          onStateChange={setFilterState}
        />
      )}
      {filters.length > 0 && visibleRooms.length + visibleOtherRooms.length === 0 ? (
        <NoFilteredRooms
          copy={copy}
          onClear={() => {
            setFilterState({ filters: [], toggled: undefined });
          }}
        />
      ) : null}
      {visibleRooms.map((room) => (
        <HotelRoomGroup
          key={room.id}
          room={room}
          copy={copy}
          {...(context ? { context } : {})}
          {...(selectedRateId ? { selectedRateId } : {})}
          selectionConfirmed={selectionConfirmed}
          receipt={receipt}
          disabled={disabled}
          onRateSelect={onRateSelect}
          onDetailsOpen={(roomId) => {
            onRoomDetailsOpen(roomId);
            setDetailRoomId(roomId);
          }}
        />
      ))}
      <OtherRoomsSection
        rooms={visibleOtherRooms}
        copy={copy}
        disabled={disabled}
        {...otherRoomsProps(context, selectedRateId)}
        selectionConfirmed={selectionConfirmed}
        onRateSelect={onRateSelect}
        onDetailsOpen={(roomId) => {
          onRoomDetailsOpen(roomId);
          setDetailRoomId(roomId);
        }}
      />
      {detailRoom && (
        <HotelRoomDetailModal
          room={detailRoom}
          copy={copy}
          {...(context ? { context } : {})}
          onClose={() => {
            setDetailRoomId(undefined);
          }}
        />
      )}
    </section>
  );
}
